import { getGroupedFonts } from '@/components/UI/FontFamilySelector/fontFamilies';

// Every colour the editors offer, referenced by key from option lists and presets.
export const COLORS = {
    navy: '#001426',
    paper: '#fefefe',
    white: '#ffffff',
    cream: '#fef7ce',
    lightGrey: '#e6e4df',
    powder: '#ecd3cd',
    sage: '#b5bfa1',
    terracotta: '#c2714f',
    blue: '#002a65',
    midnight: '#0b1a33',
    forest: '#1f3b2d',
    burgundy: '#6b1e2b',
    black: '#1c1c1c',
    gold: '#c9a96e',
    sepia: '#5b3a29',
};

// Option ids are stored with orders, so they must never change.
export const POSTER_BACKGROUNDS = [
    { id: 1, name: 'Fehér', labelKey: 'editor.color.white', color: COLORS.paper },
    { id: 4, name: 'Bézs', labelKey: 'editor.color.beige', color: COLORS.cream },
    { id: 5, name: 'Világosszürke', labelKey: 'editor.color.light_grey', color: COLORS.lightGrey },
    { id: 6, name: 'Púderrózsaszín', labelKey: 'editor.color.powder_pink', color: COLORS.powder },
    { id: 7, name: 'Zsályazöld', labelKey: 'editor.color.sage_green', color: COLORS.sage },
    { id: 8, name: 'Terrakotta', labelKey: 'editor.color.terracotta', color: COLORS.terracotta },
    { id: 3, name: 'Kék', labelKey: 'editor.color.blue', color: COLORS.blue },
    { id: 9, name: 'Sötétzöld', labelKey: 'editor.color.dark_green', color: COLORS.forest },
    { id: 10, name: 'Bordó', labelKey: 'editor.color.burgundy', color: COLORS.burgundy },
    { id: 2, name: 'Fekete', labelKey: 'editor.color.black', color: COLORS.black },
];

export const TEXT_COLORS = [
    { id: 0, name: 'Automatikus (háttérhez igazodik)', labelKey: 'editor.color.auto', color: null },
    { id: 1, name: 'Sötétkék', labelKey: 'editor.color.navy', color: COLORS.navy },
    { id: 2, name: 'Fehér', labelKey: 'editor.color.white', color: COLORS.white },
    { id: 3, name: 'Arany', labelKey: 'editor.color.gold', color: COLORS.gold },
    { id: 4, name: 'Bordó', labelKey: 'editor.color.burgundy', color: COLORS.burgundy },
    { id: 5, name: 'Szépia', labelKey: 'editor.color.sepia', color: COLORS.sepia },
    { id: 6, name: 'Sötétzöld', labelKey: 'editor.color.dark_green', color: COLORS.forest },
    { id: 7, name: 'Krém', labelKey: 'editor.color.cream', color: COLORS.cream },
];

// Title + message pairings shared by every editor's presets.
export const FONT_PAIRS = {
    classic: ['Figtree Bold', 'Figtree Regular'],
    elegant: ['Playfair Display', 'Cormorant Garamond'],
    garamond: ['EB Garamond', 'Nunito'],
    bold: ['Anton', 'Outfit'],
    vintage: ['Old Standard TT Bold', 'Libre Baskerville'],
    romantic: ['Dancing Script', 'Lora'],
    literary: ['Spectral', 'Work Sans'],
    artDeco: ['Poiret One', 'Open Sans'],
    velvet: ['Crimson Text', 'Libre Baskerville'],
    minimal: ['Bebas Neue', 'DM Sans'],
    nordic: ['Josefin Sans', 'Nunito'],
    warmSerif: ['Fraunces', 'Merriweather'],
    soft: ['Quicksand', 'Ubuntu'],
    beach: ['Montserrat Bold', 'Raleway'],
    condensed: ['Barlow Condensed', 'PT Serif'],
    neon: ['Orbitron', 'Manrope'],
    sporty: ['Russo One', 'Manrope'],
    facade: ['Oswald', 'Source Sans Pro'],
    playful: ['Pacifico', 'Nunito'],
    slab: ['Bitter', 'Inter'],
    poster: ['Archivo Black', 'Roboto'],
    dreamy: ['Satisfy', 'Lora'],
    handwritten: ['Caveat', 'Patrick Hand'],
    display: ['DM Serif Display', 'Poppins'],
    retro: ['Righteous', 'Poppins'],
    editorial: ['Vollkorn', 'Noto Serif'],
    futuristic: ['Audiowide', 'Inter'],
    storybook: ['Amatic SC', 'Kalam'],
};

const DARK_TEXT_LUMINANCE = 0.0064; // COLORS.navy

export function normalizeHex(color) {
    if (!color) return '';
    const hex = String(color).replace('#', '').toLowerCase();
    if (hex.length === 3) return hex.split('').map((char) => char + char).join('');
    return hex;
}

export function sameColor(a, b) {
    return normalizeHex(a) === normalizeHex(b);
}

// True when white text contrasts better with `color` than the default dark text.
export function isDarkColor(color) {
    const hex = normalizeHex(color);
    const [r, g, b] = [0, 2, 4].map((i) => {
        const channel = parseInt(hex.slice(i, i + 2), 16) / 255;
        return channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return 1.05 / (luminance + 0.05) > (luminance + 0.05) / (DARK_TEXT_LUMINANCE + 0.05);
}

export const contrastColor = (color) => (isDarkColor(color) ? COLORS.white : COLORS.navy);

// The option holding `color`, or a custom entry; no colour picks the option without one (automatic).
export function matchColor(color, options, customName = 'Egyedi szín') {
    if (!color) return options.find((option) => !option.color);
    return options.find((option) => sameColor(option.color, color)) || { id: null, name: customName, color };
}

export function fontByLabel(label) {
    return getGroupedFonts().find((font) => !font.group && font.label === label) || null;
}

export function fontPair(key) {
    const [title, message] = FONT_PAIRS[key];
    return { titleTypo: fontByLabel(title), messageTypo: fontByLabel(message) };
}

// Dropdown entry shared by the editors: `options` is editor specific, the rest feeds StylePresetSelect.
export function presetEntry(id, name, options, preview) {
    return { id, name, options, fontClass: options.titleTypo && options.titleTypo.class, preview };
}

// #rrggbb <-> { h: 0-360, s: 0-1, v: 0-1 }, for the custom colour picker.
export function hexToHsv(color) {
    const [r, g, b] = [0, 2, 4].map((i) => parseInt(normalizeHex(color).slice(i, i + 2), 16) / 255);
    const max = Math.max(r, g, b);
    const delta = max - Math.min(r, g, b);
    let h = 0;
    if (delta) {
        if (max === r) h = ((g - b) / delta) % 6;
        else if (max === g) h = (b - r) / delta + 2;
        else h = (r - g) / delta + 4;
    }
    return { h: (h * 60 + 360) % 360, s: max ? delta / max : 0, v: max };
}

export function hsvToHex({ h, s, v }) {
    const channel = (n) => {
        const k = (n + h / 60) % 6;
        const value = v - v * s * Math.max(0, Math.min(k, 4 - k, 1));
        return Math.round(value * 255)
            .toString(16)
            .padStart(2, '0');
    };
    return `#${channel(5)}${channel(3)}${channel(1)}`;
}
